import React, { useState } from 'react';

import CommonRecordingPresentation from 'js/src/components/app/recorder/CommonRecordingPresentation';
import { RECORD_MODAL_STATE } from 'js/src/components/app/recorder/types';
import type { UploadRecordingArgs } from 'js/src/components/app/recorder/useMediaRecorder';
import { useMediaRecorder } from 'js/src/components/app/recorder/useMediaRecorder';
import VoiceoverPreview from 'js/src/components/app/recorder/VoiceoverPreview';
import { UploadingContext } from 'js/src/data/uploads';
import { VoiceoverRecorder } from 'js/src/libs/recorder/voiceover-recorder';

const uploadType = UploadingContext.VoiceRecording;

type VoiceoverRecordingPresentationProps = {
    /** Stream the recording to GCS. Provide this OR `onRecordingComplete`. */
    onUploadRecording?: (args: UploadRecordingArgs) => void;
    /**
     * Capture-only mode (voice cloning): receive the finished recording's raw
     * blob instead of uploading it. Provide this OR `onUploadRecording`.
     */
    onRecordingComplete?: (blob: Blob) => void;
    initialPrompterVisible?: boolean;
    prompterDefaultText?: string;
    className?: string;
};

const noop = () => {};

const VoiceoverRecordingPresentation: React.FC<VoiceoverRecordingPresentationProps> = ({
    onUploadRecording,
    onRecordingComplete,
    initialPrompterVisible,
    prompterDefaultText,
    className,
}) => {
    const [recorder] = useState(() => new VoiceoverRecorder());

    const {
        recordModalState,
        startPage,
        checkSupportError,
        checkPermissionsError,
        prompterButton,
        prompter,
        recorderClock,
        deviceSelectorControls,
        startRecordingButton,
        pauseRecordingButton,
        resumeRecordingButton,
        finishRecordingButton,
        retakeRecordingButton,
        countdownTimer,
        uploadRecordingButton,
        recordedPreviewUrl,
        retakeOverlay,
        durationWarning,
    } = useMediaRecorder(recorder, uploadType, onUploadRecording ?? noop, {
        showCameraDeviceSelector: false,
        onRecordingComplete,
        initialPrompterVisible,
        prompterDefaultText,
    });

    const preview = <VoiceoverPreview key="voiceover-preview" />;

    let callout;
    let topLeft;
    let topCenter;
    let centerStage;
    let deviceControls;
    let bottomCenter;

    switch (recordModalState) {
        case RECORD_MODAL_STATE.START:
            centerStage = startPage;
            break;
        case RECORD_MODAL_STATE.UNSUPPORTED:
            centerStage = checkSupportError;
            break;
        case RECORD_MODAL_STATE.PERMISSION_DENIED:
            centerStage = checkPermissionsError;
            break;
        case RECORD_MODAL_STATE.READY:
            topLeft = prompterButton;
            centerStage = [preview, prompter];
            deviceControls = deviceSelectorControls;
            bottomCenter = startRecordingButton;
            break;
        case RECORD_MODAL_STATE.COUNTDOWN:
            centerStage = [preview, prompter];
            bottomCenter = countdownTimer;
            break;
        case RECORD_MODAL_STATE.RECORDING:
            callout = durationWarning;
            topLeft = prompterButton;
            topCenter = recorderClock;
            centerStage = [preview, prompter];
            bottomCenter = pauseRecordingButton;
            break;
        case RECORD_MODAL_STATE.PAUSED:
            topCenter = recorderClock;
            centerStage = [preview, prompter, retakeOverlay];
            bottomCenter = [retakeRecordingButton, resumeRecordingButton, finishRecordingButton];
            break;
        case RECORD_MODAL_STATE.FINISH_RECORDING:
            centerStage = [
                <VoiceoverPreview key="voiceover-preview" recordingUrl={recordedPreviewUrl} />,
                retakeOverlay,
            ];
            bottomCenter = [retakeRecordingButton, uploadRecordingButton];
            break;
        default:
            break;
    }

    return (
        <CommonRecordingPresentation
            callout={callout}
            topLeft={topLeft}
            topCenter={topCenter}
            centerStage={centerStage}
            deviceControls={deviceControls}
            bottomCenter={bottomCenter}
            className={className}
        />
    );
};

export default VoiceoverRecordingPresentation;
